import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import AuthenticatedSelect from '../AuthenticatedSelect';
import { updateExamField, handSideRecorded } from '../../utils/examData';
import { calculateHandWPI } from '../../utils/handCalculations';
import { useExamTab } from './ExamTabContext';

const HandsTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
    handSection, setHandSection,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Hands</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Hand Rating Section — live overall-hand WPI instrument (right column) */}
                  {examData?.hands && (
                    <div className="lg:order-2 lg:sticky lg:top-20">
                      <div className="card p-4 flex flex-col gap-4">
                        <div>
                          <span className="font-medium">Right Hand WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                            {(() => {
                              const data = examData?.hands;
                              if (!data) return "--";
                              
                              // Check if right hand has any data
                              const hasRightData = handSideRecorded(data, 'right');
                              
                              if (!hasRightData) {
                                return "--";
                              }
                              
                              // Fallback to calculation utility if narrative value not available
                              const handWPI = calculateHandWPI(data, 'right');
                              return `${handWPI}%`;
                            })()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Left Hand WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                            {(() => {
                              const data = examData?.hands;
                              if (!data) return "--";
                              
                              // Check if left hand has any data
                              const hasLeftData = handSideRecorded(data, 'left');
                              
                              if (!hasLeftData) {
                                return "--";
                              }
                              
                              // Fallback to calculation utility if narrative value not available
                              const handWPI = calculateHandWPI(data, 'left');
                              return `${handWPI}%`;
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Left column: digit sub-tabs + measurements */}
                  <div className="lg:order-1">
                  {/* Tabs for different parts of the hand */}
                  <div className="mb-8">
                    <div className="border-b border-border">
                      <nav className="-mb-px flex space-x-8" aria-label="Hand sections">
                        {[
                          { name: 'Thumbs', id: 'thumbs' },
                          { name: 'Index Fingers', id: 'index-fingers' },
                          { name: 'Middle Fingers', id: 'middle-fingers' },
                          { name: 'Ring Fingers', id: 'ring-fingers' },
                          { name: 'Little Fingers', id: 'little-fingers' },
                        ].map((tab) => (
                          <button
                            key={tab.id}
                            className={`
                              border-b-2 py-4 px-1 text-center text-sm font-medium
                              ${handSection === tab.id ? 'border-accent-500 text-accent-400' : 'border-transparent text-content-tertiary hover:border-border hover:text-content-secondary'}
                            `}
                            onClick={() => setHandSection(tab.id)}
                          >
                            {tab.name}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                  
                  {/* Thumbs Section */}
                  {handSection === 'thumbs' && (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium">Thumb Range of Motion</h4>
                        <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('thumbs', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('thumbs', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('thumbs')} className={clearConfirmRegion === 'thumbs' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'thumbs' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('thumbs')} className="btn-secondary text-xs" disabled={!user} title="Fills every digit: this one is set to normal; the others only get normals in blank fields">Fill Normal (Whole Hand)</button></div>
                      </div>
                      
                      {/* IP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">IP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">80°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_ip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_ip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_ip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_ip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">-10°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_ip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_ip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_ip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_ip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* MCP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">MCP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">60°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* CMC Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">CMC Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Radial Abduction Row */}
                          <div className="col-span-1 text-content-secondary">Radial Abduction</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">50°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_cmc_radial_abduction ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_cmc_radial_abduction', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_cmc_radial_abduction ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_cmc_radial_abduction', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Adduction Row */}
                          <div className="col-span-1 text-content-secondary">Adduction</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_cmc_adduction ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_cmc_adduction', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_cmc_adduction ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_cmc_adduction', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Opposition Row */}
                          <div className="col-span-1 text-content-secondary">Opposition</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">8 cm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_cmc_opposition ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_cmc_opposition', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_cmc_opposition ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_cmc_opposition', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Sensory Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Sensory Assessment</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Radial Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              min={0}
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              min={0}
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Radial Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <div className="relative">
                              <AuthenticatedInput
                                type="number"
                              min={0}
                              max={100}
                                className="input-field text-center"
                                value={examData?.hands?.thumbs?.right_radial_two_point_percent ?? ''}
                                onChange={(value) => {
                                  setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_radial_two_point_percent', value));
                                }}
                                user={user}
                                userProfile={userProfile}
                                onSessionStart={startManualSession}
                                onShowAuthModal={() => setShowAuthModal(true)}
                              />
                              {examData?.hands?.thumbs?.right_radial_two_point_percent !== null && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <span className="text-gray-300">%</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-span-1">
                            <div className="relative">
                              <AuthenticatedInput
                                type="number"
                              min={0}
                              max={100}
                                className="input-field text-center"
                                value={examData?.hands?.thumbs?.left_radial_two_point_percent ?? ''}
                                onChange={(value) => {
                                  setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_radial_two_point_percent', value));
                                }}
                                user={user}
                                userProfile={userProfile}
                                onSessionStart={startManualSession}
                                onShowAuthModal={() => setShowAuthModal(true)}
                              />
                              {examData?.hands?.thumbs?.left_radial_two_point_percent !== null && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <span className="text-gray-300">%</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Ulnar Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              min={0}
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              min={0}
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Ulnar Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <div className="relative">
                              <AuthenticatedInput
                                type="number"
                              min={0}
                              max={100}
                                className="input-field text-center"
                                value={examData?.hands?.thumbs?.right_ulnar_two_point_percent ?? ''}
                                onChange={(value) => {
                                  setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_ulnar_two_point_percent', value));
                                }}
                                user={user}
                                userProfile={userProfile}
                                onSessionStart={startManualSession}
                                onShowAuthModal={() => setShowAuthModal(true)}
                              />
                              {examData?.hands?.thumbs?.right_ulnar_two_point_percent !== null && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <span className="text-gray-300">%</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-span-1">
                            <div className="relative">
                              <AuthenticatedInput
                                type="number"
                              min={0}
                              max={100}
                                className="input-field text-center"
                                value={examData?.hands?.thumbs?.left_ulnar_two_point_percent ?? ''}
                                onChange={(value) => {
                                  setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_ulnar_two_point_percent', value));
                                }}
                                user={user}
                                userProfile={userProfile}
                                onSessionStart={startManualSession}
                                onShowAuthModal={() => setShowAuthModal(true)}
                              />
                              {examData?.hands?.thumbs?.left_ulnar_two_point_percent !== null && (
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <span className="text-gray-300">%</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Other Impairments Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Other Impairments</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Amputation Percent Row */}
                          <div className="col-span-1 text-content-secondary">Amputation (%)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              min={0}
                              max={100}
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              min={0}
                              max={100}
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Triggering Row */}
                          <div className="col-span-1 text-content-secondary">Triggering</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.right_triggering ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'right_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.thumbs?.left_triggering ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'thumbs'], 'left_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Index Fingers Section */}
                  {handSection === 'index-fingers' && (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium">Index Finger Range of Motion</h4>
                        <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('index_fingers', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('index_fingers', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('index_fingers')} className={clearConfirmRegion === 'index_fingers' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'index_fingers' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('index_fingers')} className="btn-secondary text-xs" disabled={!user} title="Fills every digit: this one is set to normal; the others only get normals in blank fields">Fill Normal (Whole Hand)</button></div>
                      </div>
                      
                      {/* DIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">DIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">70°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* PIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">PIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* MCP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">MCP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">90°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">-20°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sensory Testing Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Sensory Assessment</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Radial Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.index_fingers?.right_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.index_fingers?.left_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Radial Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.index_fingers?.right_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.index_fingers?.left_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Ulnar Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.index_fingers?.right_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.index_fingers?.left_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Ulnar Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.index_fingers?.right_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.index_fingers?.left_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Other Impairments Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Other Impairments</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Amputation Row */}
                          <div className="col-span-1 text-content-secondary">Amputation %</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.index_fingers?.right_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput 
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.index_fingers?.left_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Triggering Row */}
                          <div className="col-span-1 text-content-secondary">Triggering</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_triggering ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_triggering ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          
                          {/* Extensor Subluxation MCP Row */}
                          <div className="col-span-1 text-content-secondary">Extensor Subluxation MCP</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.right_extensor_subluxation_mcp ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'right_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.index_fingers?.left_extensor_subluxation_mcp ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'index_fingers'], 'left_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                        </div>
                      </div>

                    </div>
                  )}
                  
                  {/* Middle Fingers Section */}
                  {handSection === 'middle-fingers' && (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium">Middle Finger Range of Motion</h4>
                        <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('middle_fingers', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('middle_fingers', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('middle_fingers')} className={clearConfirmRegion === 'middle_fingers' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'middle_fingers' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('middle_fingers')} className="btn-secondary text-xs" disabled={!user} title="Fills every digit: this one is set to normal; the others only get normals in blank fields">Fill Normal (Whole Hand)</button></div>
                      </div>

                      {/* DIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">DIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">70°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* PIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">PIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* MCP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">MCP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">90°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">-20°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sensory Testing Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Sensory Assessment</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Radial Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.middle_fingers?.right_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.middle_fingers?.left_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Radial Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.middle_fingers?.right_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.middle_fingers?.left_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Ulnar Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.middle_fingers?.right_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.middle_fingers?.left_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Ulnar Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.middle_fingers?.right_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.middle_fingers?.left_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Other Impairments Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Other Impairments</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Amputation Row */}
                          <div className="col-span-1 text-content-secondary">Amputation (%)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.middle_fingers?.right_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.middle_fingers?.left_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Triggering Row */}
                          <div className="col-span-1 text-content-secondary">Triggering</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_triggering ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select...</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_triggering ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select...</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>

                          {/* Extensor Subluxation MCP Row */}
                          <div className="col-span-1 text-content-secondary">Extensor Subluxation MCP</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.right_extensor_subluxation_mcp ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'right_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select...</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.middle_fingers?.left_extensor_subluxation_mcp ?? ''}
                              onChange={(value: string | null) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'middle_fingers'], 'left_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select...</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                        </div>
                      </div>

                    </div>
                  )}
                  
                  {handSection === 'ring-fingers' && (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium">Ring Finger Range of Motion</h4>
                        <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('ring_fingers', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('ring_fingers', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('ring_fingers')} className={clearConfirmRegion === 'ring_fingers' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'ring_fingers' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('ring_fingers')} className="btn-secondary text-xs" disabled={!user} title="Fills every digit: this one is set to normal; the others only get normals in blank fields">Fill Normal (Whole Hand)</button></div>
                      </div>

                      {/* DIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">DIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">70°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* PIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">PIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* MCP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">MCP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">90°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">-20°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sensory Testing Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Sensory Assessment</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Radial Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.ring_fingers?.right_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.ring_fingers?.left_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Radial Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.ring_fingers?.right_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.ring_fingers?.left_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Ulnar Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.ring_fingers?.right_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.ring_fingers?.left_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Ulnar Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.ring_fingers?.right_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.ring_fingers?.left_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Other Impairments Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Other Impairments</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Amputation Row */}
                          <div className="col-span-1 text-content-secondary">Amputation %</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.ring_fingers?.right_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.ring_fingers?.left_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Triggering Row */}
                          <div className="col-span-1 text-content-secondary">Triggering</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_triggering ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_triggering ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          
                          {/* Extensor Subluxation MCP Row */}
                          <div className="col-span-1 text-content-secondary">Extensor Subluxation MCP</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.right_extensor_subluxation_mcp ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'right_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.ring_fingers?.left_extensor_subluxation_mcp ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'ring_fingers'], 'left_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                        </div>
                      </div>

                    </div>
                  )}
                  
                  {handSection === 'little-fingers' && (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium">Little Finger Range of Motion</h4>
                        <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('little_fingers', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('little_fingers', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('little_fingers')} className={clearConfirmRegion === 'little_fingers' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'little_fingers' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('little_fingers')} className="btn-secondary text-xs" disabled={!user} title="Fills every digit: this one is set to normal; the others only get normals in blank fields">Fill Normal (Whole Hand)</button></div>
                      </div>

                      {/* DIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">DIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">70°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_dip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_dip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_dip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_dip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* PIP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">PIP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_pip_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_pip_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_pip_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_pip_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* MCP Joint Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">MCP Joint</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Flexion Row */}
                          <div className="col-span-1 text-content-secondary">Flexion</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">90°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_mcp_flexion ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_mcp_flexion', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>

                          {/* Extension Row */}
                          <div className="col-span-1 text-content-secondary">Extension</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">-20°</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_mcp_extension ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_mcp_extension', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Sensory Testing Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Sensory Assessment</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Radial Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.little_fingers?.right_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.little_fingers?.left_radial_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_radial_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Radial Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Radial Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.little_fingers?.right_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.little_fingers?.left_radial_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_radial_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Ulnar Two-Point Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point (mm)</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">5 mm</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.little_fingers?.right_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              value={examData?.hands?.little_fingers?.left_ulnar_two_point ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_ulnar_two_point', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Ulnar Two-Point Impairment Row */}
                          <div className="col-span-1 text-content-secondary">Ulnar Two-Point: % of Digit Length</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">100%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.little_fingers?.right_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.little_fingers?.left_ulnar_two_point_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_ulnar_two_point_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Other Impairments Section */}
                      <div className="mb-8">
                        <h5 className="text-md font-medium mb-4 text-content-secondary border-b border-border pb-1 inline-block">Other Impairments</h5>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                          {/* Header Row */}
                          <div className="col-span-1"></div>
                          <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                          <div className="col-span-1 text-center font-medium">Right</div>
                          <div className="col-span-1 text-center font-medium">Left</div>

                          {/* Amputation Row */}
                          <div className="col-span-1 text-content-secondary">Amputation %</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">0%</div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.little_fingers?.right_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedInput
                              type="number"
                              className="input-field text-center"
                              min={0}
                              max={100}
                              value={examData?.hands?.little_fingers?.left_amputation_percent ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_amputation_percent', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            />
                          </div>
                          
                          {/* Triggering Row */}
                          <div className="col-span-1 text-content-secondary">Triggering</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_triggering ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_triggering ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_triggering', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          
                          {/* Extensor Subluxation MCP Row */}
                          <div className="col-span-1 text-content-secondary">Extensor Subluxation MCP</div>
                          <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">None</div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.right_extensor_subluxation_mcp ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'right_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                          <div className="col-span-1">
                            <AuthenticatedSelect
                              className="input-field text-center"
                              value={examData?.hands?.little_fingers?.left_extensor_subluxation_mcp ?? ''}
                              onChange={(value) => {
                                setExamData(prev => updateExamField(prev, ['hands', 'little_fingers'], 'left_extensor_subluxation_mcp', value));
                              }}
                              user={user}
                              userProfile={userProfile}
                              onSessionStart={startManualSession}
                              onShowAuthModal={() => setShowAuthModal(true)}
                            >
                              <option value="">Select</option>
                              <option value="None">None</option>
                              <option value="Mild">Mild</option>
                              <option value="Moderate">Moderate</option>
                              <option value="Severe">Severe</option>
                            </AuthenticatedSelect>
                          </div>
                        </div>
                      </div>

                    </div>
                  )}
                  </div>
                  </div>

                </div>
  );
};

export default HandsTab;
